import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { apiClient, tokenStorage } from "../../lib/apiClient";
import { markManualLogout } from "../../lib/sessionRedirect";
import { AuthContextValue, AuthUser, LoginPayload, UserBase } from "./types";

const STORAGE_KEY = "provisioning-portal/auth-user";

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const normalizeUser = (user: AuthUser) => ({
  ...user,
  fullName: [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email
});

const mapBackendUserTypeToRole = (userType: UserBase["user_type"]): AuthUser["role"] => {
  switch (userType) {
    case "superuser":
      return "superuser";
    case "owner":
      // "retailer" is the portal-facing label for store owners
      return "retailer";
    case "store_employee":
      return "store_employee";
    default:
      return "store_employee";
  }
};

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<AuthContextValue["user"]>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = tokenStorage.get();
    if (!token) {
      return;
    }

    const userResponse = await apiClient.get<UserBase>("/users/me");
    const userData = userResponse.data;

    const nextUser: AuthUser = {
      id: userData.id,
      email: userData.email,
      firstName: userData.first_name || "",
      lastName: userData.last_name || "",
      role: mapBackendUserTypeToRole(userData.user_type),
      forcePasswordReset: Boolean(userData.force_password_reset)
    };

    setUser(normalizeUser(nextUser));
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));
    }
  }, []);

  useEffect(() => {
    const restoreUser = () => {
      if (typeof window === "undefined") {
        return;
      }
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return;
      }
      try {
        const parsed = JSON.parse(raw) as AuthUser;
        setUser(normalizeUser(parsed));
      } catch (error) {
        console.warn("Failed to restore user from localStorage", error);
        window.localStorage.removeItem(STORAGE_KEY);
      }
    };

    const handleLogout = () => {
      setUser(null);
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    };

    window.addEventListener("auth:logout", handleLogout);
    restoreUser();
    setIsLoading(false);

    return () => {
      window.removeEventListener("auth:logout", handleLogout);
    };
  }, []);

  const login = useCallback(async (payload: LoginPayload) => {
    const formData = new URLSearchParams();
    formData.append("username", payload.email);
    formData.append("password", payload.password || "");

    const response = await apiClient.post<{ access_token: string; token_type: string }>(
      "/auth/login/access-token",
      formData,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        }
      }
    );

    if (response.data.access_token) {
      tokenStorage.set(response.data.access_token);
    }

    const userResponse = await apiClient.get<UserBase>("/users/me");
    const userData = userResponse.data;

    const nextUser: AuthUser = {
      id: userData.id,
      email: userData.email,
      firstName: userData.first_name || "",
      lastName: userData.last_name || "",
      role: mapBackendUserTypeToRole(userData.user_type),
      forcePasswordReset: Boolean(userData.force_password_reset)
    };

    setUser(normalizeUser(nextUser));
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));
    }
  }, []);

  const logout = useCallback(async () => {
    markManualLogout();
    try {
      await apiClient.post("/auth/logout");
    } catch (error) {
      console.error("Logout request failed", error);
    } finally {
      tokenStorage.remove();
      setUser(null);
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      login,
      logout,
      refreshUser
    }),
    [user, isLoading, login, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
